import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { getMaster, getMovements, getProject, deleteMovement, MASTER_STATUSES, MOVEMENT_TYPES, DELIVERY_MODES, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
    ArrowLeft,
    Edit,
    Printer,
    Film,
    HardDrive,
    Clock,
    Lock,
    Unlock,
    Truck,
    Plus,
    Volume2,
    Subtitles,
    Copy,
    Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function MasterDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isManager } = useAuth();
    const [master, setMaster] = useState(null);
    const [project, setProject] = useState(null);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [id]);

    const fetchData = async () => {
        try {
            const masterRes = await getMaster(id);
            setMaster(masterRes.data);
            
            const [projectRes, movementsRes] = await Promise.all([
                getProject(masterRes.data.project_id),
                getMovements({ master_id: id })
            ]);
            setProject(projectRes.data);
            setMovements(movementsRes.data);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement du master'));
            navigate('/masters');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        return `status-${status}`;
    };

    const handleDeleteMovement = async (movementId) => {
        try {
            await deleteMovement(movementId);
            setMovements(prev => prev.filter(m => m.id !== movementId));
            toast.success('Mouvement supprimé');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    if (!master) return null;

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="master-detail">
                {/* Header */}
                <div className="flex flex-wrap items-start gap-4">
                    <Button variant="ghost" size="icon" aria-label="Retour" onClick={() => navigate(-1)} data-testid="back-button">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 min-w-[12rem]">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="font-heading text-2xl font-bold tracking-tight break-all">
                                {master.name}
                            </h1>
                            {master.encrypted && (
                                <Badge className="bg-tone-ecrase-base/20 text-tone-ecrase border-tone-ecrase-base/30">
                                    <Lock className="w-3 h-3 mr-1" />
                                    Crypté
                                </Badge>
                            )}
                            <Badge variant="outline" className={getStatusBadgeClass(master.status)}>
                                {MASTER_STATUSES[master.status] || master.status}
                            </Badge>
                        </div>
                        <Link 
                            to={`/projects/${master.project_id}`}
                            className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-1"
                        >
                            <Film className="w-4 h-4" />
                            {project?.title}
                        </Link>
                    </div>
                    {isManager() && (
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => navigate(`/masters/${id}/label`)} data-testid="print-label-button">
                                <Printer className="w-4 h-4 mr-2" />
                                Étiquette
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/masters/${id}/edit`)} data-testid="edit-button">
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/projects/${master.project_id}/masters/new?duplicate=${id}`)} data-testid="duplicate-button">
                                <Copy className="w-4 h-4 mr-2" />
                                Dupliquer
                            </Button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Info */}
                    <Card className="bg-card border-border lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                                <HardDrive className="w-5 h-5" />
                                Informations
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Technical Info */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="p-4 rounded-sm bg-secondary/30">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">N° Disque</p>
                                    <p className="font-mono font-medium">{master.disk_number}</p>
                                </div>
                                <div className="p-4 rounded-sm bg-secondary/30">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Durée</p>
                                    <p className="font-mono font-medium">{master.duration_tc}</p>
                                </div>
                                <div className="p-4 rounded-sm bg-secondary/30">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Format</p>
                                    <p className="font-medium">{master.format}</p>
                                </div>
                                <div className="p-4 rounded-sm bg-secondary/30">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Fréquence</p>
                                    <p className="font-medium">{master.frame_rate}</p>
                                </div>
                            </div>

                            <Separator />

                            {/* Audio & Subtitles */}
                            <div>
                                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                                    <Volume2 className="w-4 h-4" />
                                    Pistes audio & sous-titres ({master.audio_tracks?.length || 0})
                                </h4>
                                {master.audio_tracks && master.audio_tracks.length > 0 ? (
                                    <div className="space-y-2">
                                        {master.audio_tracks.map((track, index) => (
                                            <div key={index} className="p-2.5 rounded-sm bg-secondary/30 border border-border">
                                                <Badge variant="secondary" className="text-xs">
                                                    <Volume2 className="w-3 h-3 mr-1" />
                                                    {track.language}
                                                </Badge>
                                                {track.subtitles && track.subtitles.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5 mt-1.5 pl-4">
                                                        {track.subtitles.map((sub, subIdx) => (
                                                            <Badge key={subIdx} variant="outline" className="text-xs">
                                                                <Subtitles className="w-2.5 h-2.5 mr-1" />
                                                                ST {sub}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Aucune piste audio</p>
                                )}
                            </div>

                            <Separator />

                            {/* Additional Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Version</p>
                                    <p className="font-medium">{master.version || '1'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Master précédent</p>
                                    <p className="font-mono text-sm">{master.previous_master_name || '-'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Movements */}
                    <Card className="bg-card border-border">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                                <Truck className="w-5 h-5" />
                                Mouvements
                            </CardTitle>
                            {isManager() && (
                                <Button size="sm" variant="outline" aria-label="Ajouter un mouvement" onClick={() => navigate(`/movements/new?master_id=${id}`)}>
                                    <Plus className="w-4 h-4" />
                                </Button>
                            )}
                        </CardHeader>
                        <CardContent>
                            {movements.length === 0 ? (
                                <div className="text-center py-8">
                                    <Truck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                    <p className="text-sm text-muted-foreground">Aucun mouvement</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin">
                                    {movements.map((movement) => (
                                        <div
                                            key={movement.id}
                                            className="p-3 rounded-sm bg-secondary/30 space-y-2 cursor-pointer hover:bg-secondary/50 transition-colors"
                                            data-testid={`movement-${movement.id}`}
                                            onClick={() => navigate(`/movements/${movement.id}`)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <Badge variant="outline" className={`text-xs ${
                                                    movement.type === 'reception' ? 'status-en_stock' :
                                                    movement.type === 'expedition' ? 'status-chez_client' : 'status-ecrase'
                                                }`}>
                                                    {MOVEMENT_TYPES[movement.type] || movement.type}
                                                </Badge>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={`/movements/${movement.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        aria-label={`Voir le mouvement du ${format(new Date(movement.date), 'dd/MM/yyyy', { locale: fr })}`}
                                                        className="text-xs text-muted-foreground flex items-center gap-1 hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                    >
                                                        <Clock className="w-3 h-3" aria-hidden="true" />
                                                        {format(new Date(movement.date), 'dd/MM/yyyy', { locale: fr })}
                                                    </Link>
                                                    {isManager() && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon" aria-label="Supprimer ce mouvement"
                                                            className="h-6 w-6 text-muted-foreground hover:text-destructive-text"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteMovement(movement.id); }}
                                                            data-testid={`delete-movement-${movement.id}`}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {movement.delivery_mode && (
                                                <p className="text-xs text-muted-foreground">
                                                    Mode: {DELIVERY_MODES[movement.delivery_mode] || movement.delivery_mode}
                                                </p>
                                            )}
                                            {movement.tracking_number && (
                                                <p className="text-xs font-mono text-muted-foreground">
                                                    Suivi: {movement.tracking_number}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
